import type { Item } from '@/stores/canvasStore';
import { decodeHtmlEntities } from '@/lib/htmlEntities';

/**
 * A note's body is one of two shapes. Notes typed on the phone are saved as
 * plain text (newlines and all); text cards edited on desktop, notes saved
 * from the iPhone Shortcut and anything Partnr writes are TipTap HTML. These
 * helpers read either shape, so the phone can show and edit both.
 */

/** HTML opens with a block tag an editor writes. "<3 this" typed on the phone is text. */
const HTML_START = /^\s*<(?:p|h[1-6]|ul|ol|li|blockquote|pre|div|br|hr|table)[\s>/]/i;

export function isHtmlBody(body: string): boolean {
  return HTML_START.test(body);
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * The body as HTML for the editor: HTML stays as it is; plain text becomes a
 * paragraph per blank-line-separated block, single newlines kept as line breaks.
 */
export function noteBodyToHtml(body: string): string {
  if (!body.trim()) return '<p></p>';
  if (isHtmlBody(body)) return body;
  return body
    .split(/\n{2,}/)
    .map((block) => block.replace(/^\n+|\n+$/g, ''))
    .filter((block) => block.trim())
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Readable text from either shape: blocks on their own lines, entities decoded (&amp; reads &). */
export function noteBodyToText(body: string): string {
  if (!isHtmlBody(body)) return body.trim();
  const text = body
    .replace(/<\/(p|div|li|h[1-6]|blockquote|pre)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return decodeHtmlEntities(text);
}

/** The note's raw body: `body`, or older cards' `content` / `title`. */
export function noteBody(item: Item): string {
  const c = (item.content ?? {}) as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === 'string' ? v : '');
  if (item.item_type === 'sticky') return pick(c.text);
  return pick(c.body) || pick(c.content) || pick(c.title);
}

/** The full text of a note-like item (no tile cut-off), or null for anything else. */
export function noteText(item: Item): string | null {
  if (item.item_type !== 'text_card' && item.item_type !== 'document' && item.item_type !== 'sticky') return null;
  return noteBodyToText(noteBody(item));
}

/**
 * Notes the phone can edit in place: text cards, whether written on the phone
 * or on a canvas. Stickies keep their own plain-text shape and documents their
 * own editor on desktop, so those stay read-only here.
 */
export function isEditableNote(item: Item): boolean {
  return item.item_type === 'text_card';
}
